/**
 * The filing line down the left of a thread: each message filed to patients gets a dot, and a run of messages filed to
 * the same patients is joined by a line from the patient banner above the first. A run ends where the patients change,
 * an unfiled message comes, or a new day starts, and closes with "N messages filed to …". The last run stays open,
 * running on to the composer's filing bar, while the conversation is still being filed to those patients.
 *
 * `key` is `patientContextKey` of a message's links ('' when it is not filed); `openKey` is the same for the patients
 * being filed to now ('' when filing is off). A message filed on its own (a `#` mention, or one linked and then ended)
 * gets its dot, joined only to the patient banner over it when it has one (`banner`).
 */
export interface FilingMark {
  dot: boolean;
  /** joined to the message above (or the banner over the run) */
  above: boolean;
  /** joined to what comes below: the next message, the closing line, or the open end */
  below: boolean;
}

export function filingRuns(rows: { key: string; newDay: boolean; banner?: boolean }[], openKey: string): {
  marks: FilingMark[];
  /** index of a run's last message -> how many messages the run filed */
  closedAfter: Map<number, number>;
  /** the last run is still being filed */
  open: boolean;
} {
  const marks: FilingMark[] = rows.map(() => ({ dot: false, above: false, below: false }));
  const closedAfter = new Map<number, number>();
  let open = false;
  let i = 0;
  while (i < rows.length) {
    const key = rows[i].key;
    if (!key) {
      i++;
      continue;
    }
    let end = i;
    while (end + 1 < rows.length && rows[end + 1].key === key && !rows[end + 1].newDay) end++;
    const isOpen = end === rows.length - 1 && key === openKey;
    const joined = isOpen || end > i;
    for (let k = i; k <= end; k++) marks[k] = { dot: true, above: joined || !!rows[k].banner, below: joined };
    if (isOpen) open = true;
    else if (joined) closedAfter.set(end, end - i + 1);
    i = end + 1;
  }
  return { marks, closedAfter, open };
}

/**
 * The run under a patient heading: the message `fromId` and the later ones filed to the same patients (`keyOf`), up to
 * where the patients change or a new day starts, as filingRuns closes a run.
 */
export function runFrom<T extends { id: string; createdAt: string }>(messages: readonly T[], fromId: string, keyOf: (m: T) => string): T[] {
  const start = messages.findIndex((m) => m.id === fromId);
  if (start < 0) return [];
  const key = keyOf(messages[start]);
  const day = new Date(messages[start].createdAt).toDateString();
  const out: T[] = [];
  for (let i = start; i < messages.length; i++) {
    const m = messages[i];
    if (keyOf(m) !== key || new Date(m.createdAt).toDateString() !== day) break;
    out.push(m);
  }
  return out;
}
